import React from 'react';
import { Layer, Source } from 'react-map-gl';
import { DIVISION_BOUNDRIES } from '../../../../../appConstants';
import { createSelector } from 'reselect';

interface IMapboxFieldLayerProps {
    polygonId : string;
    strokeId : string;
    labelId : string;

    polygonBeforeId ?: string;
    strokeBeforeId ?: string;
    labelBeforeId ?: string;

    minZoom ?: number;
    maxZoom ?: number;

    divisionGuid ?: string | null;
    subDivisionGuid ?: string | null;
    departmentGuid ?: string | null;
    fieldGuid ?: string | null;

    visible ?: boolean;
}

interface IMapboxFieldLayerState {}

export default class MapboxFieldLayer extends React.PureComponent<IMapboxFieldLayerProps, IMapboxFieldLayerState> {
    constructor(props : IMapboxFieldLayerProps) {
        super(props);
        this.state = {};
    }

    private readonly getDivisionGuid = (props : IMapboxFieldLayerProps) => props.divisionGuid;
    private readonly getSubDivisionGuid = (props : IMapboxFieldLayerProps) => props.subDivisionGuid;
    private readonly getDepartmentGuid = (props : IMapboxFieldLayerProps) => props.departmentGuid;
    private readonly getFieldGuid = (props : IMapboxFieldLayerProps) => props.fieldGuid;

    private getFilter = createSelector(
        [
            this.getDivisionGuid,
            this.getSubDivisionGuid,
            this.getFieldGuid,
            this.getDepartmentGuid,
        ],
        (
            divisionGuid,
            subDivisionGuid,
            departmentGuid,
            fieldGuid,
        ) => {
            const result : Array<unknown> = [];

            if (divisionGuid) {
                result.push(['==', ['literal', divisionGuid], ['get', 'fldDivisionGuid']]);
            }

            if (subDivisionGuid) {
                result.push(['==', ['literal', subDivisionGuid], ['get', 'fldSubDivisionGuid']]);
            }

            if (departmentGuid) {
                result.push(['==', ['literal', departmentGuid], ['get', 'fldDepartmentGuid']]);
            }

            if (fieldGuid) {
                result.push(['==', ['literal', fieldGuid], ['get', 'fldGuid']]);
            }
            
            return [
                'all',
                ...result,
            ];
        },
    );

    public readonly render = () => {
        const {
            minZoom = 0,
            maxZoom = 20,
            labelId,
            strokeId,
            polygonId,
            labelBeforeId,
            polygonBeforeId,
            strokeBeforeId,
            visible = true,
        } = this.props;

        const filter = this.getFilter(this.props);

        return (
            <Source
                id='polygon_field_source'
                type='vector'
                tiles={[
                    `${GEO_SERVER_URL}/geoserver/${GEO_SERVER_WORKSPACE}/gwc/service/wmts?REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&LAYER=farm_fields&STYLE=&TILEMATRIX=EPSG:900913:{z}&TILEMATRIXSET=EPSG:900913&FORMAT=application/vnd.mapbox-vector-tile&TILECOL={x}&TILEROW={y}&TILED=false`,
                ]}
                minzoom={minZoom}
                maxzoom={maxZoom}
                bounds={DIVISION_BOUNDRIES}
            >
                <Layer {...{
                    id: polygonId,
                    'source-layer': 'farm_fields',
                    type: 'fill',
                    minzoom: minZoom,
                    maxzoom: maxZoom,
                    'layout': {
                        visibility: visible ? 'visible' : 'none',
                    },
                    'paint': {
                        'fill-color': ['get', 'fldColor'],
                        'fill-opacity': 0.2,
                    },
                    'road-label-simple': true,
                    filter,
                    beforeId: polygonBeforeId,
                }}/>
                <Layer {...{
                    id: strokeId,
                    'source-layer': 'farm_fields',
                    'type': 'line',
                    minzoom: minZoom,
                    maxzoom: maxZoom,
                    'layout': {
                        visibility: visible ? 'visible' : 'none',
                    },
                    'paint': {
                        'line-color': ['get', 'fldColor'],
                        'line-width': 3,
                    },
                    filter,
                    beforeId: strokeBeforeId,
                }}/>
                <Layer {...{
                    id: labelId,
                    'source-layer': 'farm_fields',
                    type: 'symbol',
                    minzoom: minZoom,
                    maxzoom: maxZoom,
                    layout: {
                        visibility: visible ? 'visible' : 'none',
                        'text-field': [
                            'format',
                            [
                                'get',
                                'fldName',
                            ],
                            {
                                'font-scale': 0.8,
                            },
                        ],
                        'text-justify': 'center',
                        'text-allow-overlap': false,
                    },
                    paint: {
                        'text-opacity': 1,
                        'text-color': '#FFFFFF',
                        'text-halo-color': '#000000',
                        'text-halo-width': 2,
                        'text-halo-blur': 1,
                    },
                    filter,
                    beforeId: labelBeforeId,
                }}
                />
            </Source>
        );
    };
}
